import { Fragment, useEffect, useRef, useState } from "react";
import GeneralBtn from "../../../../components/GeneralBtn";
import { Box } from "../../../../components/MuiGenerals";
import { useTranslation } from "../../../../hooks";
import { genField } from "../../../../utils/HelperComp";
import { customSx } from "../../../../utils/styling";
import {
  translatePrefix,
  translateKeyObj as TK,
  initParams,
  IFilterFields,
  initFilterFields,
} from "../config";
import { useReduxContext, setLiquidityProviderParams } from "../reduxContext";
import { ChainSingleSelection } from "../../../../components/ChainSelection";
import { AssetMultiSelection } from "../../../../components/AssetSelection";
import { useAssets, useChains } from "../../../../components/FetchConfig";
import { NodeTypesEnumMultiSelection } from "../../../../components/GeneralSelection";
import { useAppSelector } from "../../../../reducer/hooks";
import { selectCrossMerchant } from "../../../../reducer/crossMerchantSlice";
import { selectProfile } from "../../../../reducer/profileSlice";
import MpTextField from "../../../../components/MpTextField";

interface RenderField {
  [key: string]: string;
}

const ViewFilter = () => {
  const [_, dispatch] = useReduxContext();

  const [fields, setFields] = useState<IFilterFields>(initFilterFields);

  const merchantFromInputRef = useRef<HTMLInputElement>(null);
  const merchantLPInputRef = useRef<HTMLInputElement>(null);

  const { merchantId } = useAppSelector(selectProfile);

  const { t } = useTranslation(translatePrefix);

  const { selectChainByPieces } = useChains();

  const { selectAssetNamesByChain } = useAssets();
  const allCurrecniesFrom = selectAssetNamesByChain({
    chain_name: fields.fromChainName,
  });
  const allCurrecniesTo = selectAssetNamesByChain({
    chain_name: fields.toChainName,
  });

  const { merchantMapping, liquidityProvidersMapping } =
    useAppSelector(selectCrossMerchant);

  const getParams = () => {
    const { toMerchantIds, fromChainName } = fields;

    const { chain_id: fromChainId, chain_type: fromChainType } =
      selectChainByPieces({ chain_name: fromChainName });

    return {
      ...initParams,
      ...fields,
      fromChainId,
      fromChainType,
      toMerchantIds,
    };
  };

  const apiParams = getParams();

  const onSearch = async () => {
    dispatch(setLiquidityProviderParams(apiParams));
  };

  const onReset = () => {
    setFields(initFilterFields);
  };

  useEffect(() => {
    if (!merchantId || !merchantFromInputRef.current) {
      return;
    }

    merchantFromInputRef.current.value = String(
      merchantMapping[merchantId] || ""
    );
  }, [merchantMapping]);

  useEffect(() => {
    if (!merchantId || !merchantLPInputRef.current) {
      return;
    }

    merchantLPInputRef.current.value = String(
      merchantMapping[merchantId] || ""
    );
  }, [merchantMapping]);

  const filterFields: RenderField = genField({ t }, [
    [
      TK.merchantFrom,
      <NodeTypesEnumMultiSelection
        label={t(TK.phMerchantFrom)}
        onChange={(merchantIds) =>
          setFields({
            ...fields,
            fromMerchantIds: merchantIds,
          })
        }
        value={fields.fromMerchantIds}
        enumData={merchantMapping}
        nameFn={(name) => name}
        isNoSorting
      />,
    ],
    [
      TK.merchantTo,
      <NodeTypesEnumMultiSelection
        label={t(TK.phMerchantTo)}
        onChange={(toMerchantIds) =>
          setFields({
            ...fields,
            toMerchantIds: toMerchantIds,
          })
        }
        value={fields.toMerchantIds}
        enumData={merchantMapping}
        nameFn={(name) => name}
        isNoSorting
      />,
    ],
    [
      TK.chainNameFrom,
      <ChainSingleSelection
        label={t(TK.phChainNameFrom)}
        setChoice={(chainName) =>
          setFields({
            ...fields,
            fromChainName: chainName,
            fromAssetNames: [],
          })
        }
        choice={fields.fromChainName}
      />,
    ],
    [
      TK.chainNameTo,
      <ChainSingleSelection
        label={t(TK.phChainNameTo)}
        setChoice={(chainName) =>
          setFields({
            ...fields,
            toChainName: chainName,
            toAssetNames: [],
          })
        }
        choice={fields.toChainName}
      />,
    ],
    [
      TK.currencyFrom,
      <AssetMultiSelection
        label={t(TK.phCurrencyFrom)}
        setChoices={(assetName) => {
          setFields({
            ...fields,
            fromAssetNames: assetName,
          });
        }}
        choices={fields.fromAssetNames}
        allItems={allCurrecniesFrom}
      />,
    ],
    [
      TK.currencyTo,
      <AssetMultiSelection
        label={t(TK.phCurrencyTo)}
        setChoices={(assetName) => {
          setFields({
            ...fields,
            toAssetNames: assetName,
          });
        }}
        choices={fields.toAssetNames}
        allItems={allCurrecniesTo}
      />,
    ],
    [
      TK.merchantLp,
      <MpTextField
        defaultValue={merchantId}
        inputRef={merchantLPInputRef}
        disabled
        sx={{
          input: {
            WebkitTextFillColor: "#000!important",
            color: "#000!important",
          },
          div: { backgroundColor: "#fff!important" },
        }}
      />,
    ],
    [
      TK.liquidityProvider,
      <NodeTypesEnumMultiSelection
        label={t(TK.phLiquidityProvider)}
        onChange={(liquidityProviders) =>
          setFields({
            ...fields,
            liquidityProviders: liquidityProviders,
          })
        }
        value={fields.liquidityProviders}
        enumData={liquidityProvidersMapping}
        nameFn={(name) => name}
        isNoSorting
      />,
    ],
  ]);

  const renderFields = () =>
    Object.keys(filterFields).map((key, index) => (
      <Fragment key={index}>{filterFields[key]}</Fragment>
    ));

  return (
    <>
      <Box sx={customSx.gridFilter} className="gridFilter">
        {renderFields()}
      </Box>
      <Box sx={customSx.filterB}>
        <GeneralBtn label="search" onClick={onSearch} />
        <GeneralBtn label="reset" onClick={onReset} />
      </Box>
    </>
  );
};
export default ViewFilter;
